/**
 * Terms of Service — attorney-approved copy (Effective Date: 2026-08-31).
 * Structure mirrors Refunds.jsx to keep the policies section visually
 * cohesive. SMS Communications Program Terms live at the bottom of the
 * same page so a single legal doc governs the whole storefront.
 */
export default function Terms() {
  return (
    <div className="max-w-[820px] mx-auto px-6 lg:px-12 py-14 lg:py-20" data-testid="terms-page">
      <p className="overline mb-4">Policy</p>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-6">Terms &amp; Conditions.</h1>
      <p className="text-[13px] font-mono uppercase tracking-[0.22em] text-stone mb-10">
        Effective Date: August 31, 2026
      </p>
      <div className="hairline mb-10" />

      <div className="text-charcoal text-[16px] md:text-[17px] leading-[1.75] space-y-6">
        <p>
          By accessing or using this website, you agree to comply with and be bound by the following
          Terms &amp; Conditions. These terms govern all purchases and use of materials obtained through
          BULKBUYKITS.COM/RESEARCH 1617 (&ldquo;BULKBUYKITS/RESEARCH 1617,&rdquo; &ldquo;we,&rdquo;
          &ldquo;our,&rdquo; or &ldquo;us&rdquo;).
        </p>

        <Section title="1. Eligibility">
          <p>
            You must be at least twenty-one (21) years of age to access this website, create an account,
            or purchase any products from BULKBUYKITS.COM/RESEARCH 1617.
          </p>
          <p>
            <strong>Research Use Only (RUO) Notice.</strong> BULKBUYKITS.COM/RESEARCH 1617 operates
            under a Research Use Only (RUO) designation consistent with the U.S. Food and Drug
            Administration&rsquo;s regulatory framework for research chemicals and reference materials.
            We supply chemical materials intended strictly for laboratory and scientific research
            purposes. Our products are for in vitro research only and are not approved by the FDA for
            human or animal consumption, medical use, diagnostic application, or therapeutic
            application. All materials offered are designed for qualified research professionals,
            laboratories, and institutions that understand and follow appropriate safety and handling
            procedures.
          </p>
          <p>By purchasing from this website, you affirm and warrant that:</p>
          <ol className="list-[lower-alpha] pl-6 space-y-1.5">
            <li>You are at least twenty-one (21) years of age;</li>
            <li>You are acquiring these products solely for lawful laboratory research and not for personal use, consumption, or administration of any kind;</li>
            <li>You will not resell, distribute, or otherwise transfer these products to any individual or entity for personal use, consumption, or administration;</li>
            <li>You are responsible for ensuring your possession and use of these products complies with all applicable federal, state, and local laws.</li>
          </ol>
        </Section>

        <Section title="2. Product Use Restrictions">
          <p>
            All products sold by BULKBUYKITS.COM/RESEARCH 1617 are strictly intended for scientific,
            laboratory, or in vitro research use only. Products are not for human or animal consumption,
            therapeutic, diagnostic, or medical application under any circumstances. No products sold by
            BULKBUYKITS.COM/RESEARCH 1617 have been evaluated or approved by the U.S. Food and Drug
            Administration for any human or veterinary application. No statements made on this website
            should be construed as medical advice, treatment guidance, dosing recommendations, or product
            endorsements. Any reference to peptide research or scientific literature is provided for
            educational context only and does not constitute a recommendation for use.
          </p>
        </Section>

        <Section title="3. Intellectual Property">
          <p>
            All content on this website, including logos, branding, product descriptions, product
            photography, certificates of analysis, and media, are the intellectual property of
            BULKBUYKITS.COM/RESEARCH 1617 and may not be copied, distributed, reproduced, or used for
            any commercial purpose without prior written consent.
          </p>
        </Section>

        <Section title="4. Order Acceptance & Right of Refusal">
          <p>
            Orders may be refused, canceled, or issued store credit in lieu of a refund at our sole
            discretion if we suspect:
          </p>
          <ol className="list-[lower-alpha] pl-6 space-y-1.5">
            <li>Misuse or intended misuse of products;</li>
            <li>High chargeback or fraud risk;</li>
            <li>Resale or distribution to unauthorized parties;</li>
            <li>Suspected human or animal consumption;</li>
            <li>Unlawful intent or violation of any applicable law;</li>
            <li>Non-compliance with these Terms.</li>
          </ol>
          <p>
            BULKBUYKITS.COM/RESEARCH 1617 reserves the right to request proof of institutional or
            research affiliation before fulfilling any order and to limit order quantities at our
            discretion.
          </p>
        </Section>

        <Section title="5. Account Terms">
          <p>Access to BULKBUYKITS.COM requires an account. By creating an account, you agree to:</p>
          <ol className="list-[lower-alpha] pl-6 space-y-1.5">
            <li>Provide accurate, current, and complete information during registration;</li>
            <li>Maintain the confidentiality of your account credentials;</li>
            <li>Notify BULKBUYKITS.COM/RESEARCH 1617 immediately of any unauthorized access to your account;</li>
            <li>Accept responsibility for all activities that occur under your account.</li>
          </ol>
          <p>
            BULKBUYKITS.COM/RESEARCH 1617 reserves the right to suspend or terminate accounts at our
            sole discretion, including for inactivity, suspected violations of these Terms, or any
            conduct we deem inconsistent with the lawful operation of our research supply business. Upon
            account termination, your right to access the website ends immediately. Order history,
            payment records, and personal data will be retained or deleted in accordance with the
            Privacy Policy and applicable law.
          </p>
        </Section>

        <Section title="6. Shipping & Delivery">
          <p>
            Orders are typically shipped within 1&ndash;2 business days after payment confirmation.
            BULKBUYKITS.COM/RESEARCH 1617 is not responsible for delays caused by third-party shipping
            carriers, customs clearance, force majeure events, weather, or any circumstance beyond our
            reasonable control. Title and risk of loss pass to the buyer upon delivery of the package to
            the carrier.
          </p>
        </Section>

        <Section title="7. Refunds & Returns">
          <p>
            We accept returns of unused, unopened products within fourteen (14) days of the original
            purchase date. Upon receipt of qualifying returned items, you will receive store credit,
            minus the original shipping cost and a 10% restocking fee. BULKBUYKITS.COM/RESEARCH 1617
            reserves the right to refuse returns where the integrity of the product cannot be verified,
            where evidence of tampering exists, or where the return does not meet the conditions above.
          </p>
          <p>
            BULKBUYKITS.COM/RESEARCH 1617 is not held liable for damaged or lost-in-transit packages
            once they leave our facility. If a package arrives damaged or does not arrive after a
            reasonable transit window, contact{" "}
            <a
              href="mailto:support@bulkbuykits.com"
              className="text-oxblood hover:text-charcoal underline"
            >
              support@bulkbuykits.com
            </a>{" "}
            and we will work with you and the carrier to trace the shipment.
          </p>
        </Section>

        <Section title="8. Disclaimer of Warranties">
          <p className="uppercase text-[14px] tracking-wide">
            All products and services are provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;
            without warranty of any kind, express or implied. BULKBUYKITS.COM/RESEARCH 1617 expressly
            disclaims all warranties, including without limitation the implied warranties of
            merchantability, fitness for a particular purpose, title, and non-infringement.
          </p>
          <p>
            BULKBUYKITS.COM/RESEARCH 1617 makes no warranty that products will meet any specific
            research objective, produce any particular research result, or be suitable for any particular
            research application. The buyer assumes all responsibility for determining the suitability
            of any product for their intended research use.
          </p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p className="uppercase text-[14px] tracking-wide">
            To the maximum extent permitted by applicable law, BULKBUYKITS.COM/RESEARCH 1617, its
            officers, directors, employees, agents, and affiliates shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages, including without limitation loss
            of profits, data, use, goodwill, or other intangible losses, resulting from:
          </p>
          <ol className="list-[lower-alpha] pl-6 space-y-1.5">
            <li>Your access to, use of, or inability to use the website or products;</li>
            <li>Any conduct or content of third parties;</li>
            <li>Misuse, improper handling, or unlawful application of any product;</li>
            <li>Unauthorized access to or alteration of your transmissions or data.</li>
          </ol>
          <p className="uppercase text-[14px] tracking-wide">
            In no event shall BULKBUYKITS.COM/RESEARCH 1617 total liability arising out of or related to
            these Terms or your use of the website exceed the greater of (i) the amount paid by you to
            BULKBUYKITS.COM/RESEARCH 1617 in the twelve (12) months preceding the event giving rise to
            the claim, or (ii) one hundred dollars ($100.00).
          </p>
          <p>
            Any claim or cause of action arising out of or related to these Terms or the products
            purchased must be filed within one (1) year after such claim or cause of action arose, or be
            permanently barred.
          </p>
        </Section>

        <Section title="10. Indemnification">
          <p>
            You agree to defend, indemnify, and hold harmless BULKBUYKITS.COM/RESEARCH 1617, its
            officers, directors, employees, agents, affiliates, suppliers, and licensors from and
            against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or
            fees (including reasonable attorneys&rsquo; fees) arising out of or related to:
          </p>
          <ol className="list-[lower-alpha] pl-6 space-y-1.5">
            <li>Your violation of these Terms;</li>
            <li>Your violation of any applicable law or regulation;</li>
            <li>Your misuse, improper handling, or unlawful application of any product purchased;</li>
            <li>Any human or animal consumption of products purchased from BULKBUYKITS.COM/RESEARCH 1617;</li>
            <li>Your resale or distribution of products to unauthorized parties;</li>
            <li>Your violation of any third-party rights, including intellectual property or privacy rights.</li>
          </ol>
        </Section>

        <Section title="11. Dispute Resolution and Arbitration">
          <p>
            <strong>Informal Resolution.</strong> Before filing any claim against BULKBUYKITS.COM/RESEARCH
            1617, you agree to attempt to resolve the dispute informally by contacting{" "}
            <a href="mailto:support@bulkbuykits.com" className="text-oxblood hover:text-charcoal underline">
              support@bulkbuykits.com
            </a>
            . The parties shall negotiate in good faith for at least sixty (60) days before initiating
            any formal proceeding.
          </p>
          <p>
            <strong>Binding Arbitration.</strong> Any dispute, claim, or controversy arising out of or
            related to these Terms, your use of the website, or any product purchased from
            BULKBUYKITS.COM/RESEARCH 1617 shall be resolved through final and binding arbitration
            administered by the American Arbitration Association (AAA) under its Commercial Arbitration
            Rules. Arbitration shall take place in Nashville, TN, unless the parties mutually agree to
            another location.
          </p>
          <p>
            <strong>Class Action Waiver.</strong>{" "}
            <span className="uppercase text-[14px] tracking-wide">
              You agree that any dispute resolution proceeding will be conducted only on an individual
              basis and not in a class, consolidated, or representative action.
            </span>{" "}
            You waive any right to participate in a class action lawsuit or class-wide arbitration
            against BULKBUYKITS.COM/RESEARCH 1617.
          </p>
          <p>
            <strong>Exceptions.</strong> Either party may bring an individual action in small claims
            court. Nothing in this section prevents either party from seeking injunctive relief in a
            court of competent jurisdiction to protect intellectual property rights.
          </p>
        </Section>

        <Section title="12. Governing Law">
          <p>
            These Terms and Conditions shall be governed by and construed in accordance with the laws of
            the State of Tennessee, without regard to its conflict of law principles. Any legal action
            not subject to arbitration shall be filed exclusively in the state or federal courts located
            in Nashville, TN, and you consent to the personal jurisdiction of such courts.
          </p>
        </Section>

        <Section title="13. Force Majeure">
          <p>
            BULKBUYKITS.COM/RESEARCH 1617 shall not be liable for any failure or delay in performance
            resulting from causes beyond our reasonable control, including without limitation acts of
            God, natural disasters, pandemics, government actions, war, terrorism, labor disputes,
            supply chain disruptions, internet or telecommunications failures, or carrier delays.
          </p>
        </Section>

        <Section title="14. Severability">
          <p>
            If any provision of these Terms is found to be invalid, illegal, or unenforceable, the
            remaining provisions shall continue in full force and effect, and the invalid provision
            shall be modified to the minimum extent necessary to make it enforceable while preserving
            the parties&rsquo; intent.
          </p>
        </Section>

        <Section title="15. Entire Agreement">
          <p>
            These Terms, together with the Privacy Policy and any other policies referenced herein,
            constitute the entire agreement between you and BULKBUYKITS.COM/RESEARCH 1617 regarding the
            subject matter hereof and supersede all prior or contemporaneous agreements,
            representations, or understandings.
          </p>
        </Section>

        <Section title="16. Amendments">
          <p>
            BULKBUYKITS.COM/RESEARCH 1617 reserves the right to update or revise these Terms &amp;
            Conditions at any time. Changes will be effective immediately upon posting to this website
            with an updated effective date. Continued use of the website following any changes
            constitutes acceptance of the modified Terms.
          </p>
        </Section>

        <Section title="17. Contact Information">
          <p>For questions regarding these Terms &amp; Conditions, contact:</p>
          <address className="not-italic">
            BULKBUYKITS.COM / RESEARCH 1617<br />
            1617 17th Avenue South<br />
            Nashville, TN 37212<br />
            <a
              href="mailto:support@bulkbuykits.com"
              className="text-oxblood hover:text-charcoal underline"
              data-testid="terms-support-email"
            >
              support@bulkbuykits.com
            </a>
          </address>
        </Section>

        <div className="hairline my-12" />

        <p className="overline mb-2">Addendum</p>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2 mb-6">
          SMS Communications Program Terms.
        </h2>

        <SmsSection title="1. Program Description">
          BULKBUYKITS.COM/RESEARCH 1617 operates an SMS marketing and transactional messaging program
          (the &ldquo;Program&rdquo;). By providing your mobile telephone number and affirmatively
          opting in via our website, checkout process, or any other BULKBUYKITS.COM/RESEARCH 1617
          intake channel, you consent to receive recurring automated marketing and transactional text
          messages from BULKBUYKITS.COM/RESEARCH 1617 at the number provided.
        </SmsSection>

        <SmsSection title="2. Consent">
          Your consent to receive SMS messages is not a condition of purchasing any products or services
          from BULKBUYKITS.COM/RESEARCH 1617. By providing your mobile number and opting in, you affirm
          that you are the subscriber of the number provided or have authority to consent on behalf of
          the subscriber.
        </SmsSection>

        <SmsSection title="3. Eligibility">
          The Program is available only to individuals (a) located in the United States, (b) at least
          twenty-one (21) years of age, and (c) with a valid mobile telephone subscription with a
          participating carrier. By opting in, you represent and warrant that you meet these eligibility
          requirements.
        </SmsSection>

        <SmsSection title="4. Message Types">
          Messages sent under the Program may include, without limitation: order confirmations, shipping
          notifications, account-related alerts, restock and inventory updates, promotional offers, new
          product announcements, research community communications, and other communications related to
          BULKBUYKITS.COM/RESEARCH 1617 products and services.
        </SmsSection>

        <SmsSection title="5. Message Frequency">
          Message frequency varies based on your account activity and active promotional campaigns. You
          may receive up to fifteen (15) messages per month. BULKBUYKITS.COM/RESEARCH 1617 reserves the
          right to adjust message frequency at its discretion.
        </SmsSection>

        <SmsSection title="6. Message and Data Rates">
          Message and data rates may apply based on your mobile carrier&rsquo;s plan and your usage.
          BULKBUYKITS.COM/RESEARCH 1617 is not responsible for any charges incurred from your mobile
          carrier as a result of your participation in the Program.
        </SmsSection>

        <SmsSection title="7. Opt-Out Instructions">
          You may opt out of marketing SMS communications at any time by replying STOP, END, QUIT,
          CANCEL, UNSUBSCRIBE, or REVOKE to any message received from BULKBUYKITS.COM/RESEARCH 1617.
          Upon receipt of your opt-out request, you will receive a single confirmation message
          acknowledging your withdrawal of consent, after which no further marketing messages will be
          sent to your number. Transactional messages directly related to active orders (such as
          shipping confirmations) may continue to be sent in accordance with applicable law.
        </SmsSection>

        <SmsSection title="8. Assistance">
          For assistance with the Program, reply HELP to any message received from BULKBUYKITS.COM/RESEARCH
          1617, or contact our support team at support@bulkbuykits.com.
        </SmsSection>

        <SmsSection title="9. Carrier Disclaimer">
          BULKBUYKITS.COM/RESEARCH 1617 messages are supported by all major U.S. mobile carriers.
          Carriers are not liable for delayed or undelivered messages. BULKBUYKITS.COM/RESEARCH 1617 is
          not responsible for delays, failures, or disruptions in message delivery resulting from
          carrier issues, network outages, technical malfunctions, or any other circumstances beyond our
          reasonable control.
        </SmsSection>

        <SmsSection title="10. Privacy">
          Information collected through the Program is governed by BULKBUYKITS.COM/RESEARCH 1617 Privacy
          Policy, which is incorporated herein by reference and available at{" "}
          <a href="/policies/privacy" className="text-oxblood hover:text-charcoal underline">
            /policies/privacy
          </a>
          . BULKBUYKITS.COM/RESEARCH 1617 does not sell, rent, or share mobile telephone numbers with
          third parties for their independent marketing purposes. Numbers may be shared with service
          providers (including our SMS platform vendor and shipping carriers) solely to operate the
          Program and fulfill your orders.
        </SmsSection>

        <SmsSection title="11. Modification of Program Terms">
          BULKBUYKITS.COM/RESEARCH 1617 reserves the right to modify, suspend, or terminate the Program,
          or any portion thereof, at any time and without prior notice. Continued participation in the
          Program following any modification constitutes acceptance of the modified terms.
        </SmsSection>

        <SmsSection title="12. Termination by BULKBUYKITS.COM">
          BULKBUYKITS.COM/RESEARCH 1617 reserves the right to remove any participant from the Program at
          any time, with or without notice, at our sole discretion.
        </SmsSection>

        <SmsSection title="13. TCPA and SHAFT Compliance">
          BULKBUYKITS.COM/RESEARCH 1617 is committed to compliance with the Telephone Consumer
          Protection Act (TCPA), the CAN-SPAM Act, the SHAFT (Sex, Hate, Alcohol, Firearms, Tobacco)
          content guidelines, and all applicable federal, state, and carrier regulations governing SMS
          communications.
        </SmsSection>

        <SmsSection title="14. Dispute Resolution">
          Any dispute arising out of or relating to the Program shall be governed by and resolved in
          accordance with the dispute resolution and arbitration provisions set forth in Section 11 of
          the Terms &amp; Conditions.
        </SmsSection>

        <SmsSection title="15. Governing Law">
          The Program and these SMS-specific terms shall be governed by and construed in accordance with
          the laws of the State of Colorado, without regard to its conflict of law principles.
        </SmsSection>

        <SmsSection title="16. Contact Information">
          For questions regarding the Program or these SMS terms, contact:{" "}
          <a href="mailto:support@bulkbuykits.com" className="text-oxblood hover:text-charcoal underline">
            support@bulkbuykits.com
          </a>
          .
        </SmsSection>

        <div className="hairline my-12" />

        <p className="text-[15px] text-stone">
          Questions? Reach us at{" "}
          <a
            href="mailto:support@bulkbuykits.com"
            className="text-oxblood hover:text-charcoal underline"
            data-testid="terms-footer-email"
          >
            support@bulkbuykits.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-8 mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function SmsSection({ title, children }) {
  return (
    <div>
      <h3 className="font-serif text-lg md:text-xl tracking-tight mt-5 mb-2">{title}</h3>
      <p>{children}</p>
    </div>
  );
}
